import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { ArrowLeft, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const STATUS_META = {
  operational: { label: "Opérationnel", color: "#059669", dot: "bg-[#059669]" },
  degraded_performance: { label: "Performance dégradée", color: "#F59E0B", dot: "bg-[#F59E0B]" },
  partial_outage: { label: "Interruption partielle", color: "#EA580C", dot: "bg-[#EA580C]" },
  major_outage: { label: "Interruption majeure", color: "#E60000", dot: "bg-[#E60000]" },
};

const SEVERITY_LABEL = { info: "Info", minor: "Mineur", major: "Majeur", critical: "Critique" };
const STATUS_LABEL = { investigating: "Investigation", identified: "Identifié", monitoring: "Monitoring", resolved: "Résolu" };

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/status-page")
      .then((r) => setData(r.data))
      .catch(() => setData({ services: [], active: [], recent: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#002FA7]" /></div>;

  const globalOK = (data?.active || []).length === 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-[#DEE2E6] bg-white">
        <div className="max-w-[1100px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/pag-logo.png" alt="PAG" className="h-9 w-9" />
            <span className="font-display font-black text-lg">PAG</span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#868E96] border-l border-[#DEE2E6] pl-3">Statut</span>
          </Link>
          <Link to="/" className="text-sm font-semibold flex items-center gap-2 hover:text-[#002FA7]"><ArrowLeft className="h-4 w-4" />Accueil</Link>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-10 lg:py-16" data-testid="status-page">
        <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-semibold mb-3">// Système PAG Business Manager</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter mb-6">État des services</h1>

        {/* Overall status */}
        <div className={`p-6 lg:p-8 mb-8 border-2 ${globalOK ? "border-[#059669] bg-[#ECFDF5]" : "border-[#E60000] bg-[#FEF2F2]"}`} data-testid="global-status">
          <div className="flex items-center gap-4">
            {globalOK ? <CheckCircle2 className="h-10 w-10 text-[#059669]" /> : <AlertTriangle className="h-10 w-10 text-[#E60000]" />}
            <div>
              <div className={`font-display font-black text-2xl tracking-tighter ${globalOK ? "text-[#065F46]" : "text-[#7F1D1D]"}`}>
                {globalOK ? "Tous les services fonctionnent normalement" : `${data.active.length} incident(s) en cours`}
              </div>
              <div className="text-sm text-[#495057] mt-1">Dernière vérification : {new Date().toLocaleString("fr-FR")}</div>
            </div>
          </div>
        </div>

        {/* Active incidents */}
        {(data?.active || []).length > 0 && (
          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E60000] font-bold mb-3">Incidents en cours</div>
            <div className="space-y-3">
              {data.active.map((inc) => (
                <div key={inc.id} className="bg-white border-l-4 border-[#E60000] p-5 shadow-sm" data-testid={`active-${inc.id}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-[#FEF2F2] text-[#7F1D1D] px-2 py-0.5 border border-[#FCA5A5]">{SEVERITY_LABEL[inc.severity] || inc.severity}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-900 px-2 py-0.5">{STATUS_LABEL[inc.status] || inc.status}</span>
                  </div>
                  <div className="font-display font-bold text-lg">{inc.title}</div>
                  {inc.message && <div className="text-sm text-[#495057] mt-1 whitespace-pre-line">{inc.message}</div>}
                  {inc.updates?.length > 0 && (
                    <div className="mt-3 border-t border-[#DEE2E6] pt-2 space-y-1 text-xs">
                      {inc.updates.slice().reverse().slice(0, 5).map((u, i) => (
                        <div key={i} className="flex gap-2">
                          <span className="font-mono text-[10px] text-[#868E96]">{new Date(u.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                          <span className="font-semibold text-[10px]">{STATUS_LABEL[u.status] || u.status} —</span>
                          <span>{u.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services grid */}
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-bold mb-3">Services</div>
        <div className="bg-white border border-[#DEE2E6] divide-y divide-[#DEE2E6]" data-testid="services-list">
          {(data?.services || []).map((s) => {
            const meta = STATUS_META[s.status] || STATUS_META.operational;
            return (
              <div key={s.key} className="flex items-center justify-between p-4" data-testid={`svc-${s.key}`}>
                <div className="flex items-center gap-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                  <span className="font-semibold text-sm">{s.label}</span>
                </div>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
              </div>
            );
          })}
        </div>

        {/* Recent resolved */}
        {(data?.recent || []).length > 0 && (
          <div className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-bold mb-3">Incidents récents résolus</div>
            <div className="space-y-2">
              {data.recent.map((inc) => (
                <div key={inc.id} className="bg-white border-l-4 border-[#059669] p-4" data-testid={`recent-${inc.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-800 px-2 py-0.5 border border-emerald-300">Résolu</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#868E96]">{SEVERITY_LABEL[inc.severity]}</span>
                  </div>
                  <div className="font-semibold text-sm">{inc.title}</div>
                  <div className="text-[10px] text-[#868E96] font-mono mt-1">
                    {new Date(inc.started_at).toLocaleString("fr-FR")}
                    {inc.resolved_at && ` → ${new Date(inc.resolved_at).toLocaleString("fr-FR")}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
